// @ts-nocheck
/**
 * Coloured pill badge for a user's role. One tint per role bucket, aligned
 * with the dashboard KPI tone taxonomy:
 *
 *   SUPERADMIN / SUB_SUPERADMIN  → violet   (platform hierarchy)
 *   RESELLER                     → sky      (workforce upstream)
 *   COMPANY_ADMIN                → emerald  (business)
 *   CALL_CENTRE_ADMIN            → amber    (ops management)
 *   CALL_CENTRE_TEAM_LEAD        → indigo   (mid-tier ops)
 *   CALL_CENTRE_AGENT            → slate    (front-line)
 *
 * Falls back to slate for any unknown role so the pill never breaks.
 */
import { USER_ROLES, USER_ROLES_MAP_PROFILE } from '@/utilities/constant';
import { cn } from '@/lib/utils/cn';

interface Props {
  role?: string | null;
  className?: string;
}

const TONE_CLASSES: Record<string, string> = {
  violet:
    'bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-800',
  sky:
    'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800',
  emerald:
    'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800',
  amber:
    'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800',
  indigo:
    'bg-indigo-100 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:ring-indigo-800',
  slate:
    'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:ring-slate-700',
};

export function roleTone(role?: string | null): keyof typeof TONE_CLASSES {
  if (!role) return 'slate';
  if (role === USER_ROLES.SUPERADMIN || role === USER_ROLES.SUB_SUPERADMIN) return 'violet';
  if (role === USER_ROLES.RESELLER) return 'sky';
  if (role === USER_ROLES.COMPANY_ADMIN) return 'emerald';
  if (role === USER_ROLES.CALL_CENTRE_ADMIN) return 'amber';
  if (role === USER_ROLES.CALL_CENTRE_TEAM_LEAD) return 'indigo';
  if (role === USER_ROLES.CALL_CENTRE_AGENT || role === USER_ROLES.CALL_CENTRE) return 'slate';
  return 'slate';
}

export function UserRoleBadge({ role, className }: Props) {
  const label = USER_ROLES_MAP_PROFILE[role as any] || role || '—';
  const tone = roleTone(role);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {label}
    </span>
  );
}
